import React from "react";

/**
 * Props interface for the PlaylistCard component
 */
interface CardProps {
  title: string;
  creativesCount: number;
  imageUrl?: string; // Make optional since empty playlists won't have images
  status: "Playing" | "Draft Saved";
  isScheduled?: boolean;
}

/**
 * PlaylistCard Component
 *
 * A responsive card component that displays playlist information with a stacked card design.
 * Shows the first creative's image as background, or a placeholder for empty playlists.
 * The card width adjusts to screen size while maintaining proportional stacking effect.
 *
 * @param title - The title of the playlist
 * @param creativesCount - Number of creatives in the playlist
 * @param imageUrl - Background image URL (from first creative, optional)
 * @param status - Current status of the playlist
 * @param isScheduled - Whether the playlist is scheduled (optional)
 */
const PlaylistCard: React.FC<CardProps> = ({
  title,
  creativesCount,
  imageUrl,
  status,
  isScheduled,
}) => {
  // Determine the display status and styling
  const getStatusDisplay = () => {
    if (status === "Playing" || isScheduled) {
      return {
        text: "Playing",
        color: "text-[#10B981]",
        indicator: <span className="w-2 h-2 bg-[#10B981] rounded-full mr-2" />,
      };
    } else {
      return {
        text: "Draft Saved",
        color: "text-[#FF9500]",
        indicator: null,
      };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="relative w-full aspect-[294/385]">
      {/* Stack Effect - Bottom Card (Darkest) */}
      <div
        className="absolute bg-[#606164]"
        style={{
          width: "84.35%", // 248/294 = 84.35%
          height: "100%",
          top: "5.19%", // 20/385 = 5.19%
          left: "7.82%", // 23/294 = 7.82%
          borderRadius: "min(28.42px, 7.2vw)",
          zIndex: 0,
        }}
      />

      {/* Stack Effect - Middle Card (Medium) */}
      <div
        className="absolute bg-[#46474A]"
        style={{
          width: "92.52%", // 272/294 = 92.52%
          height: "100%",
          top: "2.86%", // 11/385 = 2.86%
          left: "3.74%", // 11/294 = 3.74%
          borderRadius: "min(28.42px, 7.2vw)",
          zIndex: 1,
        }}
      />

      {/* Main Card */}
      <div
        className="w-full h-[99.48%] relative overflow-hidden z-10"
        style={{
          borderRadius: "min(28px, 7.1vw)",
          ...(imageUrl 
            ? {
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }
            : {
                background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
              }
          )
        }}
      >
        {/* Show placeholder icon only if no image */}
        {!imageUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-16 h-16 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
        )}

        {/* Gradient Overlay for Text Readability */}
        <div 
          className="absolute inset-0 rounded-[28px]" 
          style={{
            background: imageUrl 
              ? 'linear-gradient(to top, rgba(27,29,33,0.9) 0%, rgba(30,32,36,0.55) 40%, transparent 100%)'
              : 'linear-gradient(to top, rgba(27,29,33,0.7) 0%, transparent 50%)'
          }}
        />

        {/* Card Content */}
        <div className="absolute bottom-4 left-4 right-4 text-white z-10">
          {/* Playlist Title */}
          <h3 className="text-base 2xl:text-lg font-semibold leading-[1.5] mb-1">
            {title}
          </h3>

          {/* Metadata Row */}
          <div className="text-xs 2xl:text-sm flex items-center gap-1">
            {/* Creatives Count */}
            <span>
              {creativesCount === 0 ? 'No content' : `${creativesCount} creative${creativesCount > 1 ? 's' : ''}`}
            </span>

            {/* Separator */}
            <span className="mx-1">|</span>

            {/* Status Display */}
            <span
              className={`flex items-center font-medium ${statusDisplay.color}`}
            >
              {statusDisplay.indicator}
              <span
                className={statusDisplay.text === "Draft Saved" ? "italic" : ""}
              >
                {statusDisplay.text}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaylistCard;